import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { useFlag } from "@/lib/flags";
import {
  ArrowRight, CheckCircle, Sparkle, ShieldCheck, Envelope,
  Buildings, GlobeHemisphereWest, Bank as BankIcon,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const COUNTRIES = [
  "Bénin","Burkina Faso","Cameroun","Côte d'Ivoire","Ghana","Guinée","Kenya","Mali","Maroc","Niger",
  "Nigeria","Rwanda","Sénégal","Togo","Tunisie","South Africa","United Arab Emirates",
  "France","United Kingdom","United States","Canada","India","Singapore","Other",
];

const PLAN_LABELS = { starter: "Starter · 25 000 XOF/mois", professional: "Professional · 85 000 XOF/mois", enterprise: "Enterprise · Custom" };

export default function Waitlist() {
  const [params] = useSearchParams();
  const flag = useFlag("waitlist");
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: params.get("email") || "", organization: "",
    country: "", job_title: "", investor_type: "",
    intended_use_case: "", preferred_plan: params.get("plan") || "professional",
    marketing_consent: false,
    locale: (typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("fr")) ? "fr" : "en",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/waitlist/config");
        setConfig(data);
      } catch { /* graceful */ }
    })();
  }, []);

  const isFr = form.locale === "fr";
  const t = (fr, en) => (isFr ? fr : en);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error(t("Nom et email requis.", "Name and email are required."));
      return;
    }
    setBusy(true);
    try {
      await api.post("/waitlist/register", { ...form, email: form.email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      toast.error(extractApiError(err, t("Inscription impossible.", "Registration failed.")));
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6" data-testid="waitlist-success">
        <div className="max-w-md text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-teal-soft text-teal grid place-items-center mb-6">
            <CheckCircle size={28} weight="fill" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
            {t("Vous êtes sur la liste.", "You are on the list.")}
          </h1>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
            {t(
              `Votre inscription à l'accès anticipé Orixa Invest est confirmée. Nous avons envoyé un email de bienvenue à ${form.email}. Vous serez contacté en priorité dès l'ouverture complète des abonnements.`,
              `Your Orixa Invest Early Access registration is confirmed. We have sent a welcome email to ${form.email}. You will be contacted with priority when subscriptions fully open.`
            )}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="inline-flex items-center justify-center gap-2 border border-border rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="waitlist-home-link">
              {t("Retour à l'accueil", "Back to home")}
            </Link>
            <Link to={`/waitlist/status?email=${encodeURIComponent(form.email)}`} className="inline-flex items-center justify-center gap-2 border border-border rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="waitlist-status-link">
              {t("Suivre ma candidature", "Track my application")}
            </Link>
            <a href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:opacity-90 card-elev" data-testid="waitlist-book-cta">
              {t("Réserver une démonstration produit", "Book a product demo")} <ArrowRight size={14} weight="bold" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="waitlist-page">
      {/* Slim brand bar */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Logo variant="wordmark" size={30} to="/" testId="waitlist-logo" />
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, locale: isFr ? "en" : "fr" }))}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5"
            data-testid="waitlist-locale-toggle"
          >
            {isFr ? "EN" : "FR"}
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 grid grid-cols-1 lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-16 items-start">
        {/* Left column: value prop */}
        <div>
          <div className="inline-flex items-center gap-2 bg-card border border-border text-xs font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full mb-6">
            <Sparkle size={12} weight="fill" className="text-gold" />
            {t("Accès anticipé · Places limitées", "Early Access · Limited seats")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            {t("Rejoignez l'accès", "Join the")}<br />
            <span className="italic text-primary">
              {t("anticipé Orixa.", "Early Access.")}
            </span>
          </h1>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed max-w-lg">
            {t(
              "Plateforme SaaS d'intelligence de marché pour les marchés émergents. Réservez votre place prioritaire — activation dès l'ouverture complète des abonnements, tarification fondateur verrouillée 12 mois.",
              "SaaS market intelligence software for Emerging Markets. Reserve your priority seat — activation as soon as subscriptions fully open, founder-tier pricing locked for 12 months."
            )}
          </p>

          <ul className="mt-8 space-y-4">
            {[
              { i: ShieldCheck, t: t("Analyses IA institutionnelles · 6 régions EM · 10 secteurs", "Institutional AI intelligence · 6 EM regions · 10 sectors") },
              { i: BankIcon,    t: t("Activation prioritaire dès l'ouverture des abonnements",     "Priority activation on subscription opening") },
              { i: Sparkle,     t: t("Tarification fondateur verrouillée 12 mois",                 "Founder-tier pricing locked for 12 months") },
              { i: Envelope,    t: t("Contact direct avec l'équipe Ibn Miyeta Group",              "Direct contact with the Ibn Miyeta Group team") },
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <b.i size={18} weight="duotone" className="text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm leading-relaxed">{b.t}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap gap-2">
            {[
              { i: Buildings, t: t("Institutionnel", "Institutional") },
              { i: GlobeHemisphereWest, t: t("Marchés Émergents", "Emerging Markets") },
              { i: ShieldCheck, t: t("Sécurisé", "Secure") },
            ].map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 border border-border rounded-full px-3 py-1 text-xs font-medium">
                <p.i size={12} weight="bold" className="text-primary" /> {p.t}
              </span>
            ))}
          </div>

          {!flag.is("waitlist", "unknown", "free") && (
            <div className="mt-8 rounded-lg border border-border bg-teal-soft text-teal p-4 text-sm">
              <strong>{t("Bonne nouvelle :", "Good news:")}</strong>{" "}
              {t("les abonnements sont maintenant ouverts.", "subscriptions are now open.")}{" "}
              <Link to="/pricing" className="underline font-semibold">{t("Souscrire directement →", "Subscribe directly →")}</Link>
            </div>
          )}
        </div>

        {/* Right column: form */}
        <form onSubmit={submit} className="bg-card border border-border rounded-2xl p-6 sm:p-8 card-elev space-y-4" data-testid="waitlist-form">
          <div>
            <label className="text-overline text-muted-foreground">{t("Nom complet", "Full name")} *</label>
            <input
              type="text" required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="wl-full-name"
            />
          </div>

          <div>
            <label className="text-overline text-muted-foreground">{t("Email professionnel", "Work email")} *</label>
            <input
              type="email" required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="wl-email"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-overline text-muted-foreground">{t("Organisation", "Organisation")}</label>
              <input
                type="text"
                value={form.organization}
                onChange={(e) => setForm({ ...form, organization: e.target.value })}
                className="mt-1 w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="wl-organization"
              />
            </div>
            <div>
              <label className="text-overline text-muted-foreground">{t("Poste", "Job title")}</label>
              <input
                type="text"
                value={form.job_title}
                onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                className="mt-1 w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="wl-job-title"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-overline text-muted-foreground">{t("Pays", "Country")}</label>
              <select
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="mt-1 w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="wl-country"
              >
                <option value="">{t("Sélectionner…", "Select…")}</option>
                {COUNTRIES.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <div>
              <label className="text-overline text-muted-foreground">{t("Type d'investisseur", "Investor type")}</label>
              <select
                value={form.investor_type}
                onChange={(e) => setForm({ ...form, investor_type: e.target.value })}
                className="mt-1 w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="wl-investor-type"
              >
                <option value="">{t("Sélectionner…", "Select…")}</option>
                {(config?.investor_types || []).map((it) => (<option key={it.key} value={it.key}>{it.label}</option>))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-overline text-muted-foreground">{t("Plan préféré", "Preferred plan")}</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {["starter", "professional", "enterprise"].map((p) => (
                <button
                  key={p} type="button"
                  onClick={() => setForm({ ...form, preferred_plan: p })}
                  className={`rounded-lg border px-3 py-2.5 text-xs font-semibold transition-colors ${form.preferred_plan === p ? "border-primary bg-primary/10 text-primary" : "border-border bg-background hover:bg-secondary"}`}
                  data-testid={`wl-plan-${p}`}
                >
                  {PLAN_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-overline text-muted-foreground">{t("Cas d'usage envisagé", "Intended use case")}</label>
            <textarea
              rows={3}
              value={form.intended_use_case}
              onChange={(e) => setForm({ ...form, intended_use_case: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder={t("Ex : évaluer des opportunités d'investissement en Afrique de l'Ouest…", "e.g. Assess West-African investment opportunities across sectors…")}
              data-testid="wl-use-case"
            />
          </div>

          <label className="flex items-start gap-2.5 pt-2">
            <input
              type="checkbox"
              checked={form.marketing_consent}
              onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
              className="mt-0.5"
              data-testid="wl-consent"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              {t(
                "J'accepte de recevoir les communications produit et l'accès anticipé (fréquence : ~2/mois, désabonnement en 1 clic).",
                "I agree to receive product updates and Early Access communications (frequency: ~2/month, one-click unsubscribe)."
              )}
            </span>
          </label>

          <button
            type="submit"
            disabled={busy}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-3.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50 card-elev"
            data-testid="wl-submit"
          >
            {busy ? t("Inscription…", "Registering…") : t("Rejoindre l'accès anticipé", "Join Early Access")}
            <ArrowRight size={14} weight="bold" />
          </button>

          <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
            {t(
              "En rejoignant, vous acceptez nos ",
              "By joining you accept our ")}
            <Link to="/legal/terms" className="underline">{t("Conditions", "Terms")}</Link>{" & "}
            <Link to="/legal/privacy" className="underline">{t("Confidentialité", "Privacy")}</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
